// useIntersectionObserver - SSR-safe IntersectionObserver hook for WebGL freeze/thaw
// Implements ADR-0011: WebGL Freeze/Thaw Visual Continuity
import { useEffect, useRef, useState, useCallback } from 'react';
// ============================================
// SSR Guard
// ============================================
/**
 * Runtime SSR/IO-availability check (not a module constant, so tests can mock)
 */
function isSSR() {
    return typeof window === 'undefined' ||
        typeof IntersectionObserver === 'undefined';
}
/**
 * Safe IntersectionObserver constructor for SSR
 */
function createIntersectionObserver(callback, options) {
    if (isSSR())
        return null;
    return new IntersectionObserver(callback, options);
}
// ============================================
// Hook Implementation
// ============================================
export function useIntersectionObserver(options = {}) {
    const { root = null, rootMargin = '0px', threshold = [0, 0.1, 0.25, 0.5, 0.75, 1], freezeThreshold = 0.5, thawThreshold = 0.1, minFreezeDuration = 1000, debounceMs = 100, } = options;
    // State
    const [ratio, setRatio] = useState(0);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [isFrozen, setIsFrozen] = useState(false);
    const [isThawing, setIsThawing] = useState(false);
    const [visibilityState, setVisibilityState] = useState('hidden');
    // Refs
    const elementRef = useRef(null);
    const observerRef = useRef(null);
    const freezeTimeoutRef = useRef(null);
    const debounceRef = useRef(null);
    const lastFreezeTimeRef = useRef(0);
    const frozenByUserRef = useRef(false);
    const isFrozenRef = useRef(false);
    // ============================================
    // Core Logic
    // ============================================
    const updateVisibilityState = useCallback((ratio) => {
        if (ratio >= 1) {
            setVisibilityState('visible');
        }
        else if (ratio > 0) {
            setVisibilityState('partial');
        }
        else {
            setVisibilityState('hidden');
        }
    }, []);
    // Keep isFrozenRef in sync with isFrozen for observable state
    useEffect(() => {
        isFrozenRef.current = isFrozen;
    }, [isFrozen]);
    const processIntersection = useCallback((entries) => {
        const entry = entries[0];
        if (!entry)
            return;
        const newRatio = entry.intersectionRatio;
        const newIsIntersecting = entry.isIntersecting;
        setRatio(newRatio);
        setIsIntersecting(newIsIntersecting);
        updateVisibilityState(newRatio);
        // Handle freeze/thaw logic
        const now = Date.now();
        const timeSinceFreeze = now - lastFreezeTimeRef.current;
        if (newRatio <= freezeThreshold && newIsIntersecting) {
            // Should freeze
            if (!isFrozenRef.current && !frozenByUserRef.current) {
                if (timeSinceFreeze >= minFreezeDuration) {
                    isFrozenRef.current = true;
                    setIsFrozen(true);
                    lastFreezeTimeRef.current = now;
                }
            }
        }
        else if (newRatio >= thawThreshold) {
            // Should thaw
            if (isFrozenRef.current && !frozenByUserRef.current) {
                setIsThawing(true);
                // Allow time for WebGL context restoration
                setTimeout(() => {
                    isFrozenRef.current = false;
                    setIsFrozen(false);
                    setIsThawing(false);
                }, 100);
            }
        }
    }, [freezeThreshold, thawThreshold, minFreezeDuration, updateVisibilityState]);
    const handleIntersection = useCallback((entries) => {
        if (debounceMs === 0) {
            processIntersection(entries);
            return;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            processIntersection(entries);
        }, debounceMs);
    }, [debounceMs, processIntersection]);
    // ============================================
    // Observer Lifecycle
    // ============================================
    useEffect(() => {
        if (isSSR())
            return;
        // Create observer
        observerRef.current = createIntersectionObserver(handleIntersection, {
            root,
            rootMargin,
            threshold,
        });
        const currentElement = elementRef.current;
        if (currentElement && observerRef.current) {
            observerRef.current.observe(currentElement);
        }
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (freezeTimeoutRef.current) {
                clearTimeout(freezeTimeoutRef.current);
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [handleIntersection, root, rootMargin, threshold]);
    // ============================================
    // Public API
    // ============================================
    const setRef = useCallback((element) => {
        // Clean up previous observation
        if (elementRef.current && observerRef.current) {
            observerRef.current.unobserve(elementRef.current);
        }
        elementRef.current = element;
        // Observe new element
        if (element && observerRef.current) {
            observerRef.current.observe(element);
        }
    }, []);
    const freeze = useCallback(() => {
        frozenByUserRef.current = true;
        setIsFrozen(true);
        lastFreezeTimeRef.current = Date.now();
    }, []);
    const thaw = useCallback(() => {
        frozenByUserRef.current = false;
        setIsFrozen(false);
        setIsThawing(true);
        setTimeout(() => setIsThawing(false), 100);
    }, []);
    // ============================================
    // Return
    // ============================================
    return {
        ratio,
        isIntersecting,
        isFrozen,
        isThawing,
        visibilityState,
        ref: setRef,
        freeze,
        thaw,
    };
}
// ============================================
// Specialized Hooks
// ============================================
/**
 * Hook for WebGL canvas freeze/thaw with snapshot support
 */
export function useWebGLFreezeThaw(options = {}) {
    const { onFreeze, onThaw, ...observerOptions } = options;
    const onFreezeRef = useRef(onFreeze);
    onFreezeRef.current = onFreeze;
    const onThawRef = useRef(onThaw);
    onThawRef.current = onThaw;
    const observer = useIntersectionObserver(observerOptions);
    const prevFrozenRef = useRef(observer.isFrozen);
    // Track freeze/thaw state changes
    useEffect(() => {
        if (observer.isFrozen && onFreezeRef.current) {
            onFreezeRef.current();
        }
    }, [observer.isFrozen]);
    // Detect thaw transition: isFrozen went from true -> false
    useEffect(() => {
        if (prevFrozenRef.current && !observer.isFrozen && onThawRef.current) {
            onThawRef.current();
        }
        prevFrozenRef.current = observer.isFrozen;
    }, [observer.isFrozen]);
    return observer;
}
/**
 * Hook for multiple elements with shared freeze budget
 */
export function useIntersectionObserverGroup(elements, options = {}) {
    const { maxConcurrent = 4, ...observerOptions } = options;
    const [observers, setObservers] = useState(new Map());
    // This would be expanded for group management
    // For now, return individual observers
    return elements.map(el => useIntersectionObserver(observerOptions));
}
export default useIntersectionObserver;
